<div class="project-page">
  <div class="hero">
    <div>
      <p class="eyebrow">Projects</p>
      <h2>Portfolio Setup & Teaming</h2>
      <p class="sub">Create projects, assign clients and leads, and keep teams synced.</p>
    </div>
    <div class="hero-actions">
      <button mat-stroked-button color="primary" (click)="loadData()">
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
      <button mat-flat-button color="accent" (click)="resetForm()">
        <mat-icon>add</mat-icon>
        New Project
      </button>
    </div>
  </div>

  <mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

  <div class="grid">
    <section class="panel form-panel">
      <div class="panel-head">
        <div>
          <p class="eyebrow">{{ selectedProject ? 'Editing' : 'Create' }}</p>
          <h3>{{ selectedProject ? selectedProject.name : 'New Project' }}</h3>
        </div>
        <span class="chip muted">{{ selectedProject ? 'Draft changes' : 'Ready' }}</span>
      </div>

      <div class="form-grid">
        <mat-form-field appearance="outline">
          <mat-label>Project Name</mat-label>
          <input matInput [(ngModel)]="form.name" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Project Code</mat-label>
          <input matInput [(ngModel)]="form.code" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Client</mat-label>
          <mat-select [(value)]="form.clientUserId">
            <mat-option value="">Unassigned</mat-option>
            <mat-option *ngFor="let c of clients" [value]="c.id">{{ c.fullName || c.email }}</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Project Lead</mat-label>
          <mat-select [(value)]="form.leadStaffId">
            <mat-option [value]="null">None</mat-option>
            <mat-option *ngFor="let s of staffs" [value]="s.id">{{ s.fullName }}</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Start Date</mat-label>
          <input matInput type="date" [(ngModel)]="form.startDate" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>End Date</mat-label>
          <input matInput type="date" [(ngModel)]="form.endDate" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Status</mat-label>
          <mat-select [(value)]="form.status">
            <mat-option *ngFor="let s of statusOptions" [value]="s">{{ s }}</mat-option>
          </mat-select>
        </mat-form-field>

        <div class="progress-field">
          <label>Progress</label>
          <mat-progress-bar color="primary" mode="determinate" [value]="form.progressPercent || 0"></mat-progress-bar>
          <input type="number" min="0" max="100" [(ngModel)]="form.progressPercent" />
        </div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Description</mat-label>
          <textarea #projectDescriptionInput matInput rows="3" [(ngModel)]="form.description" (input)="onProjectDescriptionInput($event)" (click)="onProjectDescriptionInput($event)"></textarea>
        </mat-form-field>
        <div class="mention-box full-width" *ngIf="projectMentionSuggestions().length">
          <button type="button" class="mention-option" *ngFor="let staff of projectMentionSuggestions()" (click)="insertProjectMention(staff, projectDescriptionInput)">
            <strong>{{ staff.fullName }}</strong>
            <span>{{ staff.position || 'Staff' }}</span>
          </button>
        </div>

        <div class="media-upload full-width">
          <input #projectFileInput type="file" multiple accept="image/*,video/*,audio/*,application/pdf" (change)="onMediaSelected($event)" hidden />
          <button type="button" class="dropzone" (click)="projectFileInput.click()" (dragover)="$event.preventDefault()" (drop)="onMediaDrop($event)">
            <strong>Drop media here</strong>
            <span>or click to choose files</span>
          </button>
          <div class="media-list" *ngIf="form.mediaAttachments?.length">
            <div class="media-item" *ngFor="let media of form.mediaAttachments; let i = index">
              <div class="media-preview" [ngSwitch]="mediaKind(media.url, media.contentType)">
                <img *ngSwitchCase="'image'" [src]="media.url" [alt]="media.fileName" />
                <video *ngSwitchCase="'video'" [src]="media.url" controls></video>
                <audio *ngSwitchCase="'audio'" [src]="media.url" controls></audio>
                <button *ngSwitchDefault type="button" class="preview-link" (click)="openPreview(media)">{{ media.fileName }}</button>
              </div>
              <div class="media-actions">
                <span>{{ media.fileName }}</span>
                <button mat-stroked-button type="button" (click)="openPreview(media)">Preview</button>
                <button mat-stroked-button color="warn" type="button" (click)="removeMedia(i)">Remove</button>
              </div>
            </div>
          </div>
        </div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Team Members</mat-label>
          <mat-select multiple [(value)]="selectedTeamStaffIds">
            <mat-option *ngFor="let s of staffs" [value]="s.id">{{ s.fullName }} ({{ s.position || 'Staff' }})</mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div class="actions">
        <button mat-stroked-button (click)="resetForm()">Clear</button>
        <button mat-flat-button color="primary" (click)="saveProject()" [disabled]="saving || !canManageProjects()">
          {{ saving ? 'Saving...' : 'Save Project' }}
        </button>
      </div>
    </section>

    <section class="panel table-panel">
      <div class="panel-head">
        <div>
          <p class="eyebrow">Portfolio</p>
          <h3>Projects</h3>
        </div>
        <span class="chip muted">{{ projects.length }} total</span>
      </div>

      <table mat-table [dataSource]="projects" class="full-table mat-elevation-z1">
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef>Project</th>
          <td mat-cell *matCellDef="let row">
            <div class="row-head">
              <span class="name">{{ row.name }}</span>
              <span class="chip" [ngClass]="statusClass(row.status)">{{ row.status }}</span>
            </div>
            <div class="meta-line">
              <span>Lead: {{ row.leadStaffName || '-' }}</span>
              <span>Client: {{ row.clientName || '-' }}</span>
              <span>Media: {{ row.mediaAttachments?.length || 0 }}</span>
            </div>
            <div class="inline-media" *ngIf="row.mediaAttachments?.length">
              <button type="button" class="preview-link" *ngFor="let media of row.mediaAttachments" (click)="openPreview(media)">{{ media.fileName }}</button>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="progressPercent">
          <th mat-header-cell *matHeaderCellDef>Progress</th>
          <td mat-cell *matCellDef="let row">
            <mat-progress-bar color="primary" mode="determinate" [value]="row.progressPercent || 0"></mat-progress-bar>
            <div class="meta-line tiny">
              {{ row.progressPercent | number:'1.0-0' }}% • Tasks {{ row.completedTaskCount }}/{{ row.taskCount }}
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="teamCount">
          <th mat-header-cell *matHeaderCellDef>Team</th>
          <td mat-cell *matCellDef="let row">{{ row.teamCount }}</td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef>Actions</th>
          <td mat-cell *matCellDef="let row">
            <button mat-icon-button [disabled]="!canManageProjects()" (click)="editProject(row)">
              <mat-icon>edit</mat-icon>
            </button>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="['name','progressPercent','teamCount','actions']"></tr>
        <tr mat-row *matRowDef="let row; columns: ['name','progressPercent','teamCount','actions'];"></tr>
      </table>
    </section>
  </div>

  <section class="panel preview-panel" *ngIf="previewMedia as media">
    <div class="panel-head">
      <div>
        <p class="eyebrow">Preview</p>
        <h3>{{ media.fileName }}</h3>
      </div>
      <div class="preview-actions">
        <a mat-stroked-button [href]="media.url" target="_blank" rel="noopener">Open in New Tab</a>
        <button mat-stroked-button type="button" (click)="closePreview()">Close</button>
      </div>
    </div>
    <div class="preview-stage" [ngSwitch]="mediaKind(media.url, media.contentType)">
      <img *ngSwitchCase="'image'" [src]="media.url" [alt]="media.fileName" />
      <video *ngSwitchCase="'video'" [src]="media.url" controls></video>
      <audio *ngSwitchCase="'audio'" [src]="media.url" controls></audio>
      <object *ngSwitchCase="'pdf'" [data]="media.url" type="application/pdf" width="100%" height="520"></object>
      <div *ngSwitchDefault class="preview-fallback">Preview is not available for this file type. Use “Open in New Tab”.</div>
    </div>
  </section>
</div>
